import {
  Box,
  HStack,
  StatDownArrow,
  StatUpArrow,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  Avatar,
  type BodyColumnDef,
  DataTable,
  Link,
  UserTooltip,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { WipOrderByColumnEnum } from "@src/__generated__/urql-graphql";
import {
  LongContentCell,
  ProjectCell,
} from "@src/components/widgets/TableCell";
import { currency, percent } from "@src/utils/formatters";
import { kebabCase } from "lodash";
import { observer } from "mobx-react-lite";
import { type FunctionComponent, useMemo } from "react";
import type { ProjectWip } from "../models";
import type { Store } from "../Store";
import Cell from "./Cell";

type ProjectWipSubRow = ProjectWip["__subRows"][0];

type ProjectWipTableRow =
  | {
      id: string;
      rowType: "project";
      projectWip: ProjectWip;
      __subRows?: ProjectWipTableRow[];
    }
  | {
      id: string;
      rowType: "subRow";
      projectWip: ProjectWip;
      subRow: ProjectWipSubRow;
    };

const isProjectRow = (
  row: ProjectWipTableRow,
): row is Extract<ProjectWipTableRow, { rowType: "project" }> =>
  row.rowType === "project";

function createColumns(): BodyColumnDef<
  ProjectWipTableRow,
  WipOrderByColumnEnum
>[] {
  return [
    {
      id: "projectManager",
      orderBy: WipOrderByColumnEnum.ProjectManager,
      meta: { label: t`PM` },
      header: t`PM`,
      cell: ({ row }) => {
        if (!isProjectRow(row.original)) {
          return null;
        }

        const { projectManager } = row.original.projectWip.project;
        const { full_name, image, profile } = projectManager;

        return (
          <UserTooltip user={projectManager}>
            <Avatar
              name={full_name}
              src={image?.urls.thumbnail}
              colorScheme={profile?.hex_color}
            />
          </UserTooltip>
        );
      },
    },

    {
      id: "title",
      orderBy: WipOrderByColumnEnum.ProjectCode,
      meta: { label: t`Project` },
      header: t`Project`,
      cell: ({ row }) =>
        isProjectRow(row.original) ? (
          <ProjectCell.Title
            project={row.original.projectWip.project}
            brand={row.original.projectWip.project.brand}
            billable={row.original.projectWip.project.billable}
          />
        ) : (
          <LongContentCell maxWidth={180} tooltip={row.original.subRow.title}>
            <Link
              href={{
                pathname: "/projects/detail",
                query: { id: row.original.projectWip.id },
                hash: kebabCase(row.original.subRow.title),
              }}
            >
              {row.original.subRow.title}
            </Link>
          </LongContentCell>
        ),
    },

    {
      id: "budget",
      orderBy: WipOrderByColumnEnum.TotalBudget,
      meta: { label: t`Budget (Work)`, align: "right" },
      header: t`Budget (Work)`,
      cell: ({ row }) => {
        if (isProjectRow(row.original)) {
          return <Cell.Budget value={row.original.projectWip} />;
        }

        const { total_budget, total_budget_change, deleted } =
          row.original.subRow;
        const goodChange = total_budget_change > 0;

        return (
          !deleted && (
            <VStack align="end" spacing="0.5">
              <Text>{currency.formatByWorkspace(total_budget)}</Text>
              {total_budget_change !== 0 && (
                <HStack fontSize="xx-small" spacing="0.5">
                  {goodChange ? <StatUpArrow /> : <StatDownArrow />}
                  <Text color={goodChange ? "green.500" : "red.500"}>
                    {currency.formatByWorkspace(total_budget_change)}
                  </Text>
                </HStack>
              )}
            </VStack>
          )
        );
      },
    },

    {
      id: "workSpent",
      meta: { label: t`Work spent`, align: "center" },
      header: t`Work spent`,
      cell: ({ row }) =>
        isProjectRow(row.original) ? (
          <Cell.OurWork
            progressPercent={+row.original.projectWip.progress_percent}
            totalBudget={row.original.projectWip.total_budget}
            spentBudget={row.original.projectWip.spent_budget}
            spentBudgetPercent={+row.original.projectWip.spent_budget_percent}
            spentTime={row.original.projectWip.spent_time}
            remainingBudget={row.original.projectWip.remaining_budget}
            remainingTime={row.original.projectWip.remaining_time}
          />
        ) : !row.original.subRow.deleted ? (
          <Cell.OurWork
            progressPercent={+row.original.subRow.progress_percent}
            totalBudget={row.original.subRow.total_budget}
            spentBudget={row.original.subRow.spent_budget}
            spentBudgetPercent={+row.original.subRow.spent_budget_percent}
            spentTime={row.original.subRow.spent_time}
            remainingBudget={row.original.subRow.remaining_budget}
            remainingTime={row.original.subRow.remaining_time}
          />
        ) : (
          <Trans>Deleted budget item</Trans>
        ),
    },

    {
      id: "progress",
      orderBy: WipOrderByColumnEnum.Progress,
      meta: { label: t`Progress estimate (%)`, align: "center" },
      header: () => (
        <Tooltip label={t`Project progress estimated by project manager.`}>
          <Box>{t`Progress estimate (%)`}</Box>
        </Tooltip>
      ),
      cell: ({ row }) =>
        isProjectRow(row.original)
          ? percent.format(row.original.projectWip.progress_percent)
          : !row.original.subRow.deleted && (
              <Cell.SubmitProgress
                justify="center"
                subRow={row.original.subRow}
                projectWip={row.original.projectWip}
              />
            ),
    },

    {
      id: "progressChange",
      orderBy: WipOrderByColumnEnum.ProgressChange,
      meta: { label: t`Progress change`, align: "center" },
      header: () => (
        <Tooltip label={t`Change of project progress from previous month.`}>
          <Box>{t`Progress change`}</Box>
        </Tooltip>
      ),
      cell: ({ row }) => (
        <Cell.ProgressChange
          progressChange={
            isProjectRow(row.original)
              ? row.original.projectWip.progress_change
              : row.original.subRow.progress_change
          }
          progressChangePercent={
            isProjectRow(row.original)
              ? +row.original.projectWip.profit_estimate_percent
              : +row.original.subRow.profit_estimate_percent
          }
        />
      ),
    },

    {
      id: ACTIONS_COLUMN_ID,
      meta: { label: t`Actions` },
      header: t`Actions`,
      size: 250,
      cell: ({ row }) =>
        isProjectRow(row.original) ? (
          <Cell.RowActions row={row.original.projectWip} />
        ) : (
          !row.original.subRow.deleted && (
            <Cell.SubRowActions subRow={row.original.subRow} />
          )
        ),
    },
  ];
}

interface TableBodyProps {
  data: ProjectWip[];
  orderBy: Store["orderBy"];
}

export const Table: FunctionComponent<TableBodyProps> = observer(
  function Table({ data, orderBy }) {
    const cols = useMemo(() => createColumns(), []);

    const tableData = useMemo<ProjectWipTableRow[]>(
      () =>
        data.map((projectWip) => ({
          id: projectWip.id,
          rowType: "project",
          projectWip,
          __subRows: projectWip.__subRows.map((subRow) => ({
            id: subRow.id,
            rowType: "subRow",
            projectWip,
            subRow,
          })),
        })),
      [data],
    );

    return (
      <DataTable.Body<ProjectWipTableRow, WipOrderByColumnEnum>
        tableKey="project-wip"
        data={tableData}
        columns={cols}
        orderedBy={orderBy.clauses[0]}
        onOrderByChange={(clause) => {
          if (!clause.order) {
            orderBy.setClauses([]);
            return;
          }

          orderBy.setClauses([{ column: clause.column, order: clause.order }]);
        }}
        initialExpanded
      />
    );
  },
);
